<!-- Header — always visible -->
<div class="cc-header" (click)="expanded.set(!expanded())">
	<button
		pButton
		[text]="true"
		size="small"
		severity="secondary"
		class="cc-chevron-btn"
		(click)="$event.stopPropagation(); expanded.set(!expanded())"
	>
		<i class="pi" [class.pi-chevron-down]="expanded()" [class.pi-chevron-right]="!expanded()"></i>
	</button>
	<span class="cc-badge cc-badge--{{ item().source?.dataType ?? 'BOX' }}">{{ item().source?.dataType ?? 'UNKNOWN' }}</span>
	@if (item().name) {
		<span class="text-[11px] text-wb-text truncate">{{ item().name }}</span>
	}
	@if (item().slug) {
		<span class="font-mono text-[10px] text-wb-muted shrink-0">{{ item().slug }}</span>
	}
	@if (item().source?.dataVariable) {
		<span class="cc-var">{{ item().source.dataVariable }}</span>
	}
	@if (item().disabled) {
		<span class="cc-meta-badge cc-meta-badge--hidden">hidden</span>
	}
	@if (item().startFrame != null || item().endFrame != null) {
		<span class="cc-meta-badge cc-meta-badge--frames">{{ item().startFrame ?? '?' }}-{{ item().endFrame ?? '?' }}</span>
	}
	<span class="cc-actions" (click)="$event.stopPropagation()">
		<button pButton [text]="true" size="small" severity="secondary" [disabled]="index() === 0" (click)="requestMoveUp.emit()"><i class="pi pi-chevron-up"></i></button>
		<button pButton [text]="true" size="small" severity="secondary" [disabled]="index() === total() - 1" (click)="requestMoveDown.emit()"><i class="pi pi-chevron-down"></i></button>
		<button pButton [text]="true" size="small" severity="secondary" title="Copy to another scene/image" (click)="requestCopy.emit()"><i class="pi pi-copy"></i></button>
		<button pButton [text]="true" size="small" severity="danger" (click)="requestRemove.emit()"><i class="pi pi-trash"></i></button>
	</span>
</div>

<!-- Body — collapsible -->
@if (expanded()) {
	<div class="cc-body">

		<!-- ── Identity row (name editable, slug read-only) ────────────────── -->
		<div class="cc-section">
			<div class="cc-section-body">
				<div class="form-row">
					<div class="form-field">
						<label class="form-label">Name</label>
						<input pInputText class="w-full" [value]="item().name ?? ''"
							(input)="setName($any($event.target).value)" />
					</div>
					<div class="form-field">
						<label class="form-label">Slug</label>
						<input pInputText class="w-full font-mono" [value]="item().slug ?? ''" readonly />
					</div>
				</div>
			</div>
		</div>

		<!-- ── Source section ─────────────────────────────────────────────── -->
		<div class="cc-section">
			<div class="cc-section-header" (click)="sourceExpanded.set(!sourceExpanded())">
				<i class="pi" [class.pi-chevron-down]="sourceExpanded()" [class.pi-chevron-right]="!sourceExpanded()"></i>
				Source
			</div>
			@if (sourceExpanded()) {
				<div class="cc-section-body">
					<div class="form-row">
						<div class="form-field">
							<label class="form-label">Type</label>
							<p-select
								[options]="typeOptions"
								optionValue="value"
								appendTo="body"
								[ngModel]="item().source?.dataType ?? ''"
								(ngModelChange)="patch(d => d.source.dataType = $event)"
								styleClass="p-select-fluid"
							/>
						</div>
						<div class="form-field">
							<label class="form-label">Variable</label>
							<app-var-picker
								[value]="item().source?.dataVariable ?? null"
								(valueChange)="setVar($event ?? '')"
							/>
						</div>
						<div class="form-field form-field--check">
							<p-checkbox [inputId]="'cic-' + index() + '-masked-check'" [binary]="true" [ngModel]="item().masked ?? true" (ngModelChange)="patch(d => d.masked = $event)" />
							<label class="form-label" [attr.for]="'cic-' + index() + '-masked-check'">Masked</label>
						</div>
						<div class="form-field form-field--check">
							<p-checkbox
								[inputId]="'cic-' + index() + '-disabled-check'"
								[binary]="true"
								[ngModel]="item().disabled ?? false"
								(ngModelChange)="patch(d => d.disabled = $event)"
							/>
							<label class="form-label" [attr.for]="'cic-' + index() + '-disabled-check'">Disabled</label>
						</div>
					</div>

					<!-- TEXT-specific fields -->
					@if (item().source?.dataType === 'TEXT') {
						<div class="form-row">
							<div class="form-field flex-1">
								<label class="form-label">Static Text</label>
								<textarea
									pTextarea
									class="w-full"
									rows="3"
									[value]="item().source?.data?.text ?? ''"
									(input)="setText($any($event.target).value)"
								></textarea>
							</div>
						</div>
						<div class="form-row">
							<div class="form-field">
								<label class="form-label">Font</label>
								<div class="picker-row">
									<input
										pInputText
										class="flex-1 text-xs"
										readonly
										[value]="fileName(item().source?.data?.fontFamily)"
										[title]="item().source?.data?.fontFamily ?? ''"
									/>
									<button class="picker-btn" type="button" (click)="openFontPicker()" title="Browse fonts">
										<i class="pi pi-folder-open text-xs"></i>
									</button>
									@if (item().source?.data?.fontFamily) {
										<button class="picker-btn" type="button" (click)="clearField('fontFamily')" title="Clear font">
											<i class="pi pi-times text-xs"></i>
										</button>
									}
								</div>
							</div>
							<div class="form-field form-field--narrow">
								<label class="form-label">Size</label>
								<p-inputnumber
									[ngModel]="item().source?.data?.fontSize ?? 24"
									(ngModelChange)="setTextDataNumber('fontSize', $event)"
									styleClass="p-inputnumber-fluid"
									[showButtons]="false"
								/>
							</div>
							<div class="form-field form-field--color">
								<label class="form-label">Color</label>
								<div class="picker-row">
									<p-colorpicker
										[ngModel]="colorToHex(item().source?.data?.color)"
										(ngModelChange)="setTextDataField('color', hexToColor($event))"
										appendTo="body"
									/>
									<input pInputText class="flex-1 font-mono text-xs"
										[ngModel]="item().source?.data?.color ?? '#000000'"
										(ngModelChange)="setTextDataField('color', $event)"
										placeholder="#000000"
									/>
								</div>
							</div>
						</div>
						<div class="form-row">
							<div class="form-field">
								<label class="form-label">Align</label>
								<p-select
									[options]="alignOptions"
									optionValue="value"
									appendTo="body"
									[ngModel]="item().source?.data?.align ?? 'left'"
									(ngModelChange)="setAlign($event)"
									styleClass="p-select-fluid"
								/>
							</div>
							<div class="form-field">
								<label class="form-label">Weight</label>
								<p-select
									[options]="fontWeightOptions"
									optionValue="value"
									appendTo="body"
									[ngModel]="item().source?.data?.fontWeight ?? 'normal'"
									(ngModelChange)="setTextDataField('fontWeight', $event)"
									styleClass="p-select-fluid"
								/>
							</div>
							<div class="form-field">
								<label class="form-label">Style</label>
								<p-select
									[options]="fontStyleOptions"
									optionValue="value"
									appendTo="body"
									[ngModel]="item().source?.data?.fontStyle ?? 'normal'"
									(ngModelChange)="setTextDataField('fontStyle', $event)"
									styleClass="p-select-fluid"
								/>
							</div>
						</div>
						<div class="form-row">
							<div class="form-field">
								<label class="form-label">Letter Spacing</label>
								<p-inputnumber
									[ngModel]="item().source?.data?.letterSpacing ?? 0"
									(ngModelChange)="setTextDataNumber('letterSpacing', $event)"
									styleClass="p-inputnumber-fluid"
									[showButtons]="false"
								/>
							</div>
							<div class="form-field">
								<label class="form-label">Line Height</label>
								<p-inputnumber
									[ngModel]="item().source?.data?.lineHeight ?? 1"
									(ngModelChange)="setTextDataNumber('lineHeight', $event)"
									styleClass="p-inputnumber-fluid"
									[showButtons]="false"
									[step]="0.1"
									[minFractionDigits]="0"
									[maxFractionDigits]="2"
								/>
							</div>
							<div class="form-field">
								<label class="form-label">Padding</label>
								<p-inputnumber
									[ngModel]="item().source?.data?.padding ?? 0"
									(ngModelChange)="setTextDataNumber('padding', $event)"
									styleClass="p-inputnumber-fluid"
									[showButtons]="false"
								/>
							</div>
						</div>
						<div class="form-row">
							<div class="form-field">
								<label class="form-label">Prefix</label>
								<textarea
									pTextarea
									class="w-full"
									rows="2"
									[value]="item().source?.data?.prefix ?? ''"
									(input)="setTextDataField('prefix', $any($event.target).value)"
								></textarea>
							</div>
							<div class="form-field">
								<label class="form-label">Postfix</label>
								<textarea
									pTextarea
									class="w-full"
									rows="2"
									[value]="item().source?.data?.postfix ?? ''"
									(input)="setTextDataField('postfix', $any($event.target).value)"
								></textarea>
							</div>
						</div>
						<div class="form-row">
							<div class="form-field form-field--check">
								<p-checkbox [inputId]="'cic-' + index() + '-uppercase-check'" [binary]="true" [ngModel]="item().source?.data?.uppercase ?? false" (ngModelChange)="setDataBool('uppercase', $event)" />
								<label class="form-label" [attr.for]="'cic-' + index() + '-uppercase-check'">Uppercase</label>
							</div>
							<div class="form-field form-field--check">
								<p-checkbox [inputId]="'cic-' + index() + '-wordwrap-check'" [binary]="true" [ngModel]="item().source?.data?.wordWrap ?? false" (ngModelChange)="setDataBool('wordWrap', $event)" />
								<label class="form-label" [attr.for]="'cic-' + index() + '-wordwrap-check'">Word Wrap</label>
							</div>
							<div class="form-field form-field--check">
								<p-checkbox [inputId]="'cic-' + index() + '-dropshadow-check'" [binary]="true" [ngModel]="item().source?.data?.dropShadow ?? false" (ngModelChange)="setDataBool('dropShadow', $event)" />
								<label class="form-label" [attr.for]="'cic-' + index() + '-dropshadow-check'">Drop Shadow</label>
							</div>
						</div>
						@if (item().source?.data?.dropShadow) {
							<div class="form-row">
								<div class="form-field">
									<label class="form-label">Blur</label>
									<p-inputnumber [ngModel]="item().source?.data?.dropShadowBlur ?? 0" (ngModelChange)="setTextDataNumber('dropShadowBlur', $event)" styleClass="p-inputnumber-fluid" [showButtons]="false" />
								</div>
								<div class="form-field">
									<label class="form-label">Angle</label>
									<p-inputnumber [ngModel]="item().source?.data?.dropShadowAngle ?? 0" (ngModelChange)="setTextDataNumber('dropShadowAngle', $event)" styleClass="p-inputnumber-fluid" [showButtons]="false" [step]="0.1" [minFractionDigits]="0" [maxFractionDigits]="2" />
								</div>
								<div class="form-field">
									<label class="form-label">Distance</label>
									<p-inputnumber [ngModel]="item().source?.data?.dropShadowDistance ?? 0" (ngModelChange)="setTextDataNumber('dropShadowDistance', $event)" styleClass="p-inputnumber-fluid" [showButtons]="false" />
								</div>
								<div class="form-field form-field--color">
									<label class="form-label">Shadow Color</label>
									<p-colorpicker [ngModel]="colorToHex(item().source?.data?.dropShadowColor)" (ngModelChange)="setTextDataField('dropShadowColor', hexToColor($event))" appendTo="body" />
								</div>
								<div class="form-field">
									<label class="form-label">Hex</label>
									<input pInputText class="w-full font-mono text-xs"
										[ngModel]="item().source?.data?.dropShadowColor ?? '#000000'"
										(ngModelChange)="setTextDataField('dropShadowColor', $event)"
										placeholder="#000000"
									/>
								</div>
							</div>
						}
					}

					<!-- IMAGE-specific fields -->
					@if (item().source?.dataType === 'IMAGE') {
						<div class="form-row">
							<div class="form-field flex-1">
								<label class="form-label">Media</label>
								<div class="picker-row">
									<input pInputText class="flex-1 text-xs" readonly [value]="fileName(item().source?.data?.src)" [title]="item().source?.data?.src ?? ''" />
									<button class="picker-btn" type="button" (click)="openSrcPicker()" title="Browse files">
										<i class="pi pi-folder-open text-xs"></i>
									</button>
									@if (item().source?.data?.src) {
										<button class="picker-btn" type="button" (click)="clearField('src')" title="Clear media">
											<i class="pi pi-times text-xs"></i>
										</button>
									}
								</div>
							</div>
						</div>
					}

					<!-- VIDEO-specific fields -->
					@if (item().source?.dataType === 'VIDEO') {
						<div class="form-row">
							<div class="form-field flex-1">
								<label class="form-label">Media</label>
								<div class="picker-row">
									<input pInputText class="flex-1 text-xs" readonly [value]="fileName(item().source?.data?.src)" [title]="item().source?.data?.src ?? ''" />
									<button class="picker-btn" type="button" (click)="openSrcPicker()" title="Browse files">
										<i class="pi pi-folder-open text-xs"></i>
									</button>
									@if (item().source?.data?.src) {
										<button class="picker-btn" type="button" (click)="clearField('src')" title="Clear media">
											<i class="pi pi-times text-xs"></i>
										</button>
									}
								</div>
							</div>
						</div>
						<div class="form-row">
							<div class="form-field form-field--check">
								<p-checkbox [inputId]="'cic-' + index() + '-autoplay-check'" [binary]="true" [ngModel]="item().source?.data?.autoPlay ?? false" (ngModelChange)="setDataBool('autoPlay', $event)" />
								<label class="form-label" [attr.for]="'cic-' + index() + '-autoplay-check'">AutoPlay</label>
							</div>
							<div class="form-field form-field--check">
								<p-checkbox [inputId]="'cic-' + index() + '-loop-check'" [binary]="true" [ngModel]="item().source?.data?.loop ?? false" (ngModelChange)="setDataBool('loop', $event)" />
								<label class="form-label" [attr.for]="'cic-' + index() + '-loop-check'">Loop</label>
							</div>
							<div class="form-field form-field--check">
								<p-checkbox [inputId]="'cic-' + index() + '-persistent-check'" [binary]="true" [ngModel]="item().source?.data?.persistant ?? false" (ngModelChange)="setDataBool('persistant', $event)" />
								<label class="form-label" [attr.for]="'cic-' + index() + '-persistent-check'">Persistent</label>
							</div>
						</div>
					}

					<!-- WEBCAM-specific fields -->
					@if (item().source?.dataType === 'WEBCAM') {
						<div class="form-row">
							<div class="form-field">
								<label class="form-label">Mode</label>
								<p-select [options]="webcamModeOptions" optionValue="value" appendTo="body" [ngModel]="item().source?.data?.mode ?? 'PHOTO'" (ngModelChange)="setDataMode($event)" styleClass="p-select-fluid" />
							</div>
						</div>
					}
				</div>
			}
		</div>

		<!-- ── Placement section ──────────────────────────────────────────── -->
		<div class="cc-section">
			<div class="cc-section-header" (click)="placementExpanded.set(!placementExpanded())">
				<i class="pi" [class.pi-chevron-down]="placementExpanded()" [class.pi-chevron-right]="!placementExpanded()"></i>
				Placement
			</div>
			@if (placementExpanded()) {
				<div class="cc-section-body">
					<div class="form-row">
						<div class="form-field">
							<label class="form-label">Type</label>
							<p-select [options]="placementTypeOptionsFor(item().placement?.type ?? 'NORMAL')" optionValue="value" appendTo="body" readonly [ngModel]="item().placement?.type ?? 'NORMAL'" styleClass="p-select-fluid" />
						</div>
						<div class="form-field">
							<label class="form-label">Fit To</label>
							@if (item().placement?.type === 'PERSPECTIVE_TRANSFORM') {
								<p-select [options]="fitToOptionsPerspective" optionValue="value" appendTo="body" placeholder="not set — stretches" [ngModel]="fitToValue(item().placement)" (ngModelChange)="setFitTo($event)" styleClass="p-select-fluid" />
							} @else {
								<p-select [options]="fitToOptions" optionValue="value" appendTo="body" [ngModel]="fitToValue(item().placement) ?? ''" (ngModelChange)="setFitTo($event)" styleClass="p-select-fluid" />
							}
						</div>
					</div>
					<div class="form-row">
						<div class="form-field">
							<label class="form-label">Width</label>
							<p-inputnumber [ngModel]="item().placement?.fixedSize?.width ?? item().placement?.size?.width ?? null" (ngModelChange)="setSize('width', $event)" styleClass="p-inputnumber-fluid" [showButtons]="false" />
						</div>
						<div class="form-field">
							<label class="form-label">Height</label>
							<p-inputnumber [ngModel]="item().placement?.fixedSize?.height ?? item().placement?.size?.height ?? null" (ngModelChange)="setSize('height', $event)" styleClass="p-inputnumber-fluid" [showButtons]="false" />
						</div>
					</div>
					@if (item().placement?.type === 'PERSPECTIVE_TRANSFORM') {
						<div class="form-field" style="flex-basis: 100%">
							<div class="flex items-center gap-2 mb-1">
								<div class="mode-toggle">
									<button type="button" class="mode-btn" [class.mode-btn--active]="(item().placement?.contentType ?? 'STATIC') === 'STATIC'"
										(click)="setPlacementField('contentType', 'STATIC')">Static</button>
									<button type="button" class="mode-btn" [class.mode-btn--active]="item().placement?.contentType === 'DYNAMIC'"
										(click)="setPlacementField('contentType', 'DYNAMIC')">Dynamic</button>
								</div>
								<button type="button" class="coord-action-btn" [class.coord-action-btn--active]="aeInputOpen()"
									(click)="aeInputOpen() ? closeAeInput() : openAeInput()" title="Import from After Effects">
									<i class="pi pi-download"></i>
								</button>
								@if (hasPlacementCoords()) {
									<p-popover #placementCoordsPop>
										<div class="ae-tip-scroll" [innerHTML]="getPlacementCoordsSummary()"></div>
									</p-popover>
									<button type="button" class="coord-action-btn" (click)="exportAeToClipboard()" title="Copy as After Effects Corner Pin data">
										<i class="pi pi-upload"></i>
									</button>
									<button type="button" class="coord-action-btn" (click)="placementCoordsPop.toggle($event)" title="Show coordinates">
										<i class="pi pi-question-circle"></i>
									</button>
									<button type="button" class="coord-action-btn coord-action-btn--danger" (click)="clearPlacementCoords()" title="Clear position">
										<i class="pi pi-times"></i>
									</button>
								}
								@if (aeImportStatus(); as status) {
									<span class="ae-status" [class.ae-status--error]="status.type === 'error'">
										<i class="pi" [class.pi-check-circle]="status.type === 'ok'" [class.pi-exclamation-triangle]="status.type === 'error'"></i>
										{{ status.message }}
									</span>
								}
							</div>
							@if (aeInputOpen()) {
								<div class="ae-input-area mb-2">
									<textarea
										pTextarea
										class="ae-import-textarea w-full"
										data-ae-input="placement"
										placeholder="Paste AE Corner Pin keyframe data here (Ctrl+V)..."
										rows="4"
										(paste)="onAeTextareaPaste($event)"
										(keydown)="onAeTextareaKeydown($event, $any($event.target))"
										#aeTA
									></textarea>
									<div class="ae-input-actions">
										<button type="button" class="ae-parse-btn" (click)="importAeFromTextarea(aeTA.value)">
											<i class="pi pi-check"></i> Parse
										</button>
										<button type="button" class="ae-cancel-btn" (click)="closeAeInput()">
											<i class="pi pi-times"></i>
										</button>
										<span class="ae-hint">Ctrl+Enter · Esc</span>
									</div>
								</div>
							}
							<div class="coords-fields-group">
								@if (item().placement?.contentType === 'DYNAMIC') {
									<!-- Dynamic mode has no manual per-frame editing UI, same as scene-form's
									     actions — frames come from AE import; inspect via "Show coordinates". -->
									<p class="text-xs text-wb-muted m-0">
										@if (hasPlacementCoords()) {
											{{ placementFrameCount() }} keyframe(s) imported — use "Show coordinates" to inspect, or re-import to replace.
										} @else {
											No keyframes yet — paste AE Corner Pin data to import.
										}
									</p>
								} @else {
									@for (corner of cornerLabels; track corner.index) {
										<div class="form-row">
											<div class="form-field">
												<label class="form-label">{{ corner.label }} X</label>
												<p-inputnumber [ngModel]="getCoordAxis(corner.index, 'x')" (ngModelChange)="setCoordAxis(corner.index, 'x', $event)" styleClass="p-inputnumber-fluid" [showButtons]="false" />
											</div>
											<div class="form-field">
												<label class="form-label">{{ corner.label }} Y</label>
												<p-inputnumber [ngModel]="getCoordAxis(corner.index, 'y')" (ngModelChange)="setCoordAxis(corner.index, 'y', $event)" styleClass="p-inputnumber-fluid" [showButtons]="false" />
											</div>
										</div>
									}
								}
							</div>
						</div>
					}
				</div>
			}
		</div>

		<!-- ── Filters section ────────────────────────────────────────────── -->
		<div class="cc-section">
			<div class="cc-section-header" (click)="filtersExpanded.set(!filtersExpanded())">
				<i class="pi" [class.pi-chevron-down]="filtersExpanded()" [class.pi-chevron-right]="!filtersExpanded()"></i>
				Filters
				@if ((item().filters?.length ?? 0) > 0) {
					<span class="cc-meta-badge cc-meta-badge--frames">{{ item().filters?.length }}</span>
				}
			</div>
			@if (filtersExpanded()) {
				<div class="cc-section-body">
					<app-filters-editor
						[filters]="item().filters ?? []"
						(filtersChange)="setFilters($event)"
					/>
				</div>
			}
		</div>

		<!-- ── Frames & Opacity section ───────────────────────────────────── -->
		<div class="cc-section">
			<div class="cc-section-header" (click)="framesExpanded.set(!framesExpanded())">
				<i class="pi" [class.pi-chevron-down]="framesExpanded()" [class.pi-chevron-right]="!framesExpanded()"></i>
				Frames &amp; Opacity
			</div>
			@if (framesExpanded()) {
				<div class="cc-section-body">
					<div class="form-row items-start">
						<div class="form-field">
							<label class="form-label">Start Frame</label>
							<p-inputnumber [ngModel]="item().startFrame ?? null" (ngModelChange)="setFrameField('startFrame', $event)" styleClass="p-inputnumber-fluid" [showButtons]="false" />
						</div>
						<div class="form-field">
							<label class="form-label">End Frame</label>
							<p-inputnumber [ngModel]="item().endFrame ?? null" (ngModelChange)="setFrameField('endFrame', $event)" styleClass="p-inputnumber-fluid" [showButtons]="false" />
						</div>
						<div class="form-field">
							<label class="form-label">Opacity</label>
							<p-inputnumber [ngModel]="getOpacityPercent()" (ngModelChange)="setOpacityPercent($event)" styleClass="p-inputnumber-fluid" [showButtons]="false" [min]="0" [max]="100" [step]="1" [minFractionDigits]="0" [maxFractionDigits]="0" suffix="%" />
							<p-slider [ngModel]="getOpacityPercent()" (ngModelChange)="setOpacityPercent($event)" [min]="0" [max]="100" [step]="1" styleClass="mt-2" />
						</div>
					</div>

					<!-- PRO-215: dynamic opacity keyframes — a repeatable, collapsible list
					     (fade in and later fade out, etc.), not a single on/off toggle,
					     since the player's `opacities` field is itself an array. Each row
					     is a tinted card (.opk-item), same visual family as the filters
					     editor's .fe-filter cards — chevron, summary badges, move/delete
					     actions — collapsed by default. -->
					<label class="form-label">Dynamic Opacity Keyframes</label>
					@for (kf of item().opacities ?? []; track $index) {
						<div class="opk-item">
							<div class="opk-item-head" (click)="toggleOpacityKeyframeExpanded($index)">
								<button
									pButton
									[text]="true"
									size="small"
									severity="secondary"
									class="cc-chevron-btn"
									(click)="$event.stopPropagation(); toggleOpacityKeyframeExpanded($index)"
								>
									<i class="pi" [class.pi-chevron-down]="isOpacityKeyframeExpanded($index)" [class.pi-chevron-right]="!isOpacityKeyframeExpanded($index)"></i>
								</button>
								<span class="cc-meta-badge cc-meta-badge--frames">{{ getOpacityKeyframeFrame($index, 'startFrame') ?? '?' }} → {{ getOpacityKeyframeFrame($index, 'endFrame') ?? '?' }}</span>
								<span class="cc-meta-badge cc-meta-badge--frames">{{ getOpacityKeyframePercent($index, 'startOpacity') ?? '?' }}% → {{ getOpacityKeyframePercent($index, 'endOpacity') ?? '?' }}%</span>
								<span class="cc-actions" (click)="$event.stopPropagation()">
									<button pButton [text]="true" size="small" severity="secondary" [disabled]="$index === 0" (click)="moveOpacityKeyframe($index, -1)"><i class="pi pi-chevron-up"></i></button>
									<button pButton [text]="true" size="small" severity="secondary" [disabled]="$index === (item().opacities?.length ?? 0) - 1" (click)="moveOpacityKeyframe($index, 1)"><i class="pi pi-chevron-down"></i></button>
									<button pButton [text]="true" size="small" severity="danger" (click)="removeOpacityKeyframe($index)"><i class="pi pi-trash"></i></button>
								</span>
							</div>
							@if (isOpacityKeyframeExpanded($index)) {
								<div class="form-row">
									<div class="form-field">
										<label class="form-label">Start Frame</label>
										<p-inputnumber [ngModel]="getOpacityKeyframeFrame($index, 'startFrame')" (ngModelChange)="setOpacityKeyframeFrame($index, 'startFrame', $event)" styleClass="p-inputnumber-fluid" [showButtons]="false" />
									</div>
									<div class="form-field">
										<label class="form-label">End Frame</label>
										<p-inputnumber [ngModel]="getOpacityKeyframeFrame($index, 'endFrame')" (ngModelChange)="setOpacityKeyframeFrame($index, 'endFrame', $event)" styleClass="p-inputnumber-fluid" [showButtons]="false" />
									</div>
									<div class="form-field">
										<label class="form-label">Start Opacity</label>
										<p-inputnumber [ngModel]="getOpacityKeyframePercent($index, 'startOpacity')" (ngModelChange)="setOpacityKeyframePercent($index, 'startOpacity', $event)" styleClass="p-inputnumber-fluid" [showButtons]="false" [min]="0" [max]="100" [step]="1" [minFractionDigits]="0" [maxFractionDigits]="0" suffix="%" />
									</div>
									<div class="form-field">
										<label class="form-label">End Opacity</label>
										<p-inputnumber [ngModel]="getOpacityKeyframePercent($index, 'endOpacity')" (ngModelChange)="setOpacityKeyframePercent($index, 'endOpacity', $event)" styleClass="p-inputnumber-fluid" [showButtons]="false" [min]="0" [max]="100" [step]="1" [minFractionDigits]="0" [maxFractionDigits]="0" suffix="%" />
									</div>
								</div>
							}
						</div>
					}
					<div class="form-row">
						<button pButton [text]="true" size="small" severity="secondary" (click)="addOpacityKeyframe()"><i class="pi pi-plus"></i>Add opacity keyframe</button>
					</div>

					<div class="form-row">
						<div class="form-field form-field--check">
							<p-checkbox [inputId]="'cic-' + index() + '-hidden-check'" [binary]="true" [ngModel]="item().hidden ?? false" (ngModelChange)="patch(d => d.hidden = $event)" />
							<label class="form-label" [attr.for]="'cic-' + index() + '-hidden-check'">Hidden</label>
						</div>
					</div>
					<div class="form-row cc-divider-top">
						<div class="form-field" style="flex-basis: 100%">
							<label class="form-label">Attributes</label>
							<app-attributes-editor
								[data]="item().attributes ?? null"
								(dataChange)="setAttributes($event)"
							/>
						</div>
					</div>
				</div>
			}
		</div>

	</div>
}

<app-media-picker
	[(visible)]="pickerVisible"
	[categories]="pickerCategories()"
	[currentValue]="pickerCurrentValue()"
	(fileSelected)="onPickerSelected($event)"
/>
